import { FC, useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import type { TemplateSignatoryType } from '../../../../constants/template-signatory-type.constant';
import type { TemplateFormSchema } from '../../../../validations/edit-template.validation';

import { InfoCircle } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { Cursor, Tooltip } from '../../../../../../../components/tooltip/index';
import { useLocale } from '../../../../../../../hooks/index';
import { useInfiniteStakeholders } from '../../../../../../../hooks/stakeholder/use-infinite-stakeholders';
import { selectedCompanyStorage } from '../../../../../../../storage/company/selected-company.util';
import { StakeholderOrder } from '../../../../../../../types/stakeholder.types';
import {
  getTemplateSignatoryBaseType,
  getTemplateSignatoryLabelKey,
  GRANTEE,
  templateSignatoryTypeTranslation,
} from '../../../../constants/template-signatory-type.constant';
import { TemplateFieldType } from '../../../../enums/mapping-editor.const';
import {
  SIGNATURE_FIELD_TYPE_LABEL,
  SIGNATURE_FIELD_TYPE_OPTIONS,
} from '../../../../enums/signature-field-type-label.const';
import { StepThreeTab } from '../../../../validations/add-template.validation';
import { MappingCombobox, type MappingComboboxOption } from './mapping-combobox';

type SignatureFieldsSidebarProps = {
  control: Control<TemplateFormSchema>;
  setValue: UseFormSetValue<TemplateFormSchema>;
  signatories: TemplateSignatoryType[];
};

const SIGNER_SHOW_ALL_STAKEHOLDER_TYPES = new Set<TemplateSignatoryType>([GRANTEE]);

export const SignatureFieldsSidebar: FC<SignatureFieldsSidebarProps> = ({
  control,
  setValue,
  signatories,
}) => {
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const tabs = useWatch({ control, name: 'stepThree.tabs', defaultValue: [] }) as StepThreeTab[];
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const { stakeholders } = useInfiniteStakeholders({
    companyId: companyId ?? '',
    numberOfFetchedItems: 1000,
    search: '',
    sortOrder: StakeholderOrder.ASC,
  });
  const completedTabsCount = tabs.filter((tab) => tab.type != null && tab.roleName != null).length;
  const signerOptionsByBaseRole = useMemo(() => {
    const allStakeholders: MappingComboboxOption[] = stakeholders.map((stakeholder) => ({
      value: stakeholder.id,
      label: stakeholder.fullName,
    }));
    const byRole = stakeholders.reduce<Record<string, MappingComboboxOption[]>>(
      (acc, stakeholder) => {
        const role = stakeholder.type;
        const roleOptions = acc[role] ?? [];
        roleOptions.push({
          value: stakeholder.id,
          label: stakeholder.fullName,
        });
        acc[role] = roleOptions;
        return acc;
      },
      {},
    );

    return { allStakeholders, byRole };
  }, [stakeholders]);

  if (!tabs.length) return null;

  return (
    <aside
      className="flex w-[280px] shrink-0 flex-col rounded-xl bg-white"
      style={{
        boxShadow:
          '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
      }}
    >
      <div className="mb-4 flex items-center justify-between px-3 pt-3">
        <span className="text-xs text-gray-500">
          <AppFormattedMessage id="SIGNATURE_FIELDS" />
        </span>
        <span className="text-xs text-[#9747FF]">
          {completedTabsCount}/{tabs.length}
        </span>
      </div>
      <div className="space-y-3 overflow-y-auto px-3 pb-3">
        {tabs.map((tab, index) => {
          const baseRoleName =
            tab.roleName != null ? getTemplateSignatoryBaseType(tab.roleName) : null;
          const isGranteeRole = baseRoleName === GRANTEE;
          const shouldShowAllStakeholders =
            baseRoleName != null && SIGNER_SHOW_ALL_STAKEHOLDER_TYPES.has(baseRoleName);

          const signerOptions = shouldShowAllStakeholders
            ? signerOptionsByBaseRole.allStakeholders
            : baseRoleName != null
              ? (signerOptionsByBaseRole.byRole[baseRoleName] ?? [])
              : [];
          const signerOptionsWithFallback: MappingComboboxOption[] =
            tab.roleName != null && signerOptions.length === 0
              ? [{ value: '__not-found__', label: messagesLocale.NO_RESULTS_FOUND, disabled: true }]
              : signerOptions;

          return (
            <div className="rounded-lg border border-gray-200 bg-white p-3" key={tab.anchorString}>
              <p className="mb-3 text-xs text-[#9747FF]">{tab.anchorString}</p>
              <div className="mt-2">
                <label className="text-xs text-gray-500">
                  <AppFormattedMessage id="FIELD_TYPE" />
                </label>
                <MappingCombobox
                  onChange={(nextType) => {
                    const updated = [...tabs];
                    updated[index] = {
                      ...updated[index],
                      type: (nextType || undefined) as TemplateFieldType | undefined,
                    };
                    setValue('stepThree.tabs', updated, { shouldValidate: true });
                  }}
                  options={SIGNATURE_FIELD_TYPE_OPTIONS.map((fieldType) => ({
                    value: fieldType,
                    label: messagesLocaleFn(SIGNATURE_FIELD_TYPE_LABEL[fieldType]),
                  }))}
                  placeholder={messagesLocale.PLEASE_SELECT}
                  value={tab.type ?? ''}
                />
              </div>
              <div className="mt-2">
                <label className="text-xs text-gray-500">
                  <AppFormattedMessage id="STAKEHOLDER_TYPE" />
                </label>
                <MappingCombobox
                  onChange={(nextRoleName) => {
                    const updated = [...tabs];
                    updated[index] = {
                      ...updated[index],
                      roleName: (nextRoleName || undefined) as TemplateSignatoryType | undefined,
                      value: undefined,
                    };
                    setValue('stepThree.tabs', updated, { shouldValidate: true });
                  }}
                  options={signatories.map((signatory) => ({
                    value: signatory,
                    label:
                      messagesLocale[
                        getTemplateSignatoryLabelKey(signatory) ??
                          templateSignatoryTypeTranslation[GRANTEE]
                      ],
                  }))}
                  placeholder={messagesLocale.PLEASE_SELECT}
                  value={tab.roleName ?? ''}
                />
              </div>
              <div className="mt-2">
                <label className="text-xs text-gray-500">
                  <AppFormattedMessage id="SIGNER_OPTIONAL" />
                </label>
                {isGranteeRole ? (
                  <div className="relative flex h-9 items-center text-sm text-gray-400">
                    {messagesLocale.PLEASE_SELECT}
                    <div className="absolute right-0 top-[6px] -translate-y-1/2">
                      <Tooltip
                        className="w-[360px]"
                        content={messagesLocale.GRANTEE_SIGNER_PREASSIGN_TOOLTIP}
                        cursor={Cursor.POINTER}
                        placement="top"
                      >
                        <button className="text-gray-500" type="button">
                          <InfoCircle className="size-4" iconColor="#667085" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <MappingCombobox
                    disabled={tab.roleName == null}
                    onChange={(nextSignerId) => {
                      const updated = [...tabs];
                      updated[index] = {
                        ...updated[index],
                        value: nextSignerId || undefined,
                      };
                      setValue('stepThree.tabs', updated, { shouldValidate: true });
                    }}
                    options={signerOptionsWithFallback}
                    placeholder={messagesLocale.PLEASE_SELECT}
                    value={tab.value ?? ''}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
